import { CheckOutlined } from '@ant-design/icons';
import { Drawer, List } from 'antd';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';

import { Button, Tag } from 'components/ui';
import { flags } from 'utils/features';

import type { WidgetAvailability } from './featureGating';
import type { WidgetCategory, WidgetDefinition, WidgetId } from './types';
import { WIDGET_REGISTRY } from './widgetRegistry';

type WidgetCatalogDrawerProps = {
  open: boolean;
  /** Widgets to show in the catalog: those the account can add, plus placed ones still resolving. */
  listedWidgetIds: WidgetId[];
  /** Widgets already on the dashboard — shown as "Added" with a Remove action. */
  placedWidgetIds: Set<WidgetId>;
  /** Availability for each widget in the current account/feature context. */
  widgetAvailabilityById: Record<WidgetId, WidgetAvailability>;
  onClose: () => void;
  onAdd: (widgetId: WidgetId) => void;
  onRemove: (widgetId: WidgetId) => void;
};

const CATEGORY_ORDER: WidgetCategory[] = [
  'fleet',
  'damage',
  'tasks',
  'inspection',
  'work-orders',
  'cost',
];

const CategoryLabel = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.labelColor};
  margin: 14px 0 2px;
`;

const AddedLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.82rem;
  color: ${({ theme }) => theme.labelColor};
`;

export function WidgetCatalogDrawer({
  open,
  listedWidgetIds,
  placedWidgetIds,
  widgetAvailabilityById,
  onClose,
  onAdd,
  onRemove,
}: WidgetCatalogDrawerProps) {
  const { t } = useTranslation();
  const theme = useTheme();

  const categoryLabels: Record<WidgetCategory, string> = {
    fleet: t('navigation.fleet', 'Fleet'),
    damage: t('navigation.damages', 'Damages'),
    tasks: t('table.tasks', 'Tasks'),
    'work-orders': t('navigation.workOrders', 'Work Orders'),
    inspection: t('navigation.inspections', 'Inspections'),
    cost: t('workOrder.cost', 'Cost'),
  };

  const byCategory = listedWidgetIds.reduce(
    (acc, id) => {
      const definition = WIDGET_REGISTRY[id];
      (acc[definition.category] ??= []).push(definition);
      return acc;
    },
    {} as Partial<Record<WidgetCategory, WidgetDefinition[]>>
  );

  return (
    <Drawer
      title={t('components.addAWidget', 'Add a widget')}
      placement="right"
      width={400}
      open={open}
      onClose={onClose}
    >
      {CATEGORY_ORDER.filter(category => byCategory[category]?.length).map(category => (
        <div key={category}>
          <CategoryLabel>{categoryLabels[category]}</CategoryLabel>
          <List
            dataSource={byCategory[category]}
            renderItem={definition => {
              const isPlaced = placedWidgetIds.has(definition.id);
              const requiresRiskManagementUpgrade =
                widgetAvailabilityById[definition.id] === 'unavailable' &&
                definition.requiredFeatures.includes(flags.RISK_MANAGEMENT);
              return (
                <List.Item
                  actions={
                    isPlaced
                      ? [
                          <AddedLabel
                            key="added"
                            data-testid={`dashboard-catalog-added-${definition.id}`}
                          >
                            <CheckOutlined /> {t('components.added', 'Added')}
                          </AddedLabel>,
                          <Button
                            key="remove"
                            type="link"
                            danger
                            data-testid={`dashboard-catalog-remove-${definition.id}`}
                            onClick={() => onRemove(definition.id)}
                          >
                            {t('common.remove', 'Remove')}
                          </Button>,
                        ]
                      : [
                          <Button
                            key="add"
                            type="link"
                            disabled={requiresRiskManagementUpgrade}
                            data-testid={`dashboard-catalog-add-${definition.id}`}
                            onClick={() => {
                              if (!requiresRiskManagementUpgrade) {
                                onAdd(definition.id);
                              }
                            }}
                          >
                            {t('common.add', 'Add')}
                          </Button>,
                        ]
                  }
                >
                  <List.Item.Meta
                    title={
                      <span>
                        {definition.title}{' '}
                        <Tag color={definition.dataState === 'real' ? 'green' : 'default'}>
                          {definition.dataState === 'real'
                            ? t('components.live', 'Live')
                            : t('components.sample', 'Sample')}
                        </Tag>
                        {requiresRiskManagementUpgrade && (
                          <Tag color={theme.teal}>
                            {t(
                              'executiveDashboard.upgradeToRiskManagement',
                              'Upgrade to Risk Management'
                            )}
                          </Tag>
                        )}
                      </span>
                    }
                    description={definition.description}
                  />
                </List.Item>
              );
            }}
          />
        </div>
      ))}
    </Drawer>
  );
}
